import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { UserRole } from '@teffy/shared-types'

interface AuthState {
  token: string | null
  role: UserRole | null
  userId: string | null
  restaurantId: string | null
  restaurantName: string | null
  userName: string | null
  groupId: string | null
  groupName: string | null

  // Sesión real del SUPERADMIN, guardada mientras impersona a un restaurante
  superAdminToken: string | null
  superAdminUserId: string | null
  superAdminUserName: string | null

  setAuth: (data: {
    token: string
    role: UserRole
    userId: string
    restaurantId: string | null
    restaurantName: string | null
    userName: string
    groupId?: string | null
    groupName?: string | null
  }) => void
  clearAuth: () => void
  updateProfile: (data: { userName?: string; restaurantName?: string }) => void
  enterImpersonation: (data: {
    token: string
    userId: string
    restaurantId: string
    restaurantName: string
    userName: string
  }) => void
  exitImpersonation: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      role: null,
      userId: null,
      restaurantId: null,
      restaurantName: null,
      userName: null,
      groupId: null,
      groupName: null,
      superAdminToken: null,
      superAdminUserId: null,
      superAdminUserName: null,
      setAuth: ({ token, role, userId, restaurantId, restaurantName, userName, groupId = null, groupName = null }) =>
        set({ token, role, userId, restaurantId, restaurantName, userName, groupId, groupName }),
      clearAuth: () =>
        set({
          token: null, role: null, userId: null, restaurantId: null, restaurantName: null, userName: null,
          groupId: null, groupName: null,
          superAdminToken: null, superAdminUserId: null, superAdminUserName: null,
        }),
      updateProfile: ({ userName, restaurantName }) =>
        set((s) => ({
          userName: userName ?? s.userName,
          restaurantName: restaurantName ?? s.restaurantName,
        })),
      enterImpersonation: ({ token, userId, restaurantId, restaurantName, userName }) =>
        set((s) => ({
          // Conserva la sesión real del superadmin aunque ya se haya entrado a otro restaurante antes
          superAdminToken: s.superAdminToken ?? s.token,
          superAdminUserId: s.superAdminUserId ?? s.userId,
          superAdminUserName: s.superAdminUserName ?? s.userName,
          token,
          role: UserRole.ADMIN,
          userId,
          restaurantId,
          restaurantName,
          userName,
        })),
      exitImpersonation: () =>
        set((s) => ({
          token: s.superAdminToken,
          role: UserRole.SUPERADMIN,
          userId: s.superAdminUserId,
          restaurantId: null,
          restaurantName: null,
          userName: s.superAdminUserName,
          superAdminToken: null,
          superAdminUserId: null,
          superAdminUserName: null,
        })),
    }),
    { name: 'teffy-admin-auth' }
  )
)
